/**
 * DataElementsSection - Left section showing data elements needed to measure progress
 * Question: "How do we measure if we're solving this? What metrics matter?"
 */

import React from 'react';
import { Database, Server, BarChart3, AlertCircle } from 'lucide-react';
import { RelatedItemCard, NODE_COLORS } from './RelatedItemCard';
import type { RelatedDataElement } from './hooks/useIssueRelationships';

interface DataElementsSectionProps {
  dataElements: RelatedDataElement[];
  onDataElementClick?: (elementId: string) => void;
}

// Criticality badge styles
const CRITICALITY_STYLES: Record<string, string> = {
  essential: 'bg-red-100 text-red-700',
  important: 'bg-orange-100 text-orange-700',
  'nice-to-have': 'bg-blue-100 text-blue-700',
};

// Data type badge styles
const DATA_TYPE_STYLES: Record<string, string> = {
  metric: 'bg-green-100 text-green-700',
  dimension: 'bg-blue-100 text-blue-700',
  attribute: 'bg-purple-100 text-purple-700',
  identifier: 'bg-neutral-100 text-neutral-700',
};

export const DataElementsSection: React.FC<DataElementsSectionProps> = ({
  dataElements,
  onDataElementClick,
}) => {
  const colors = NODE_COLORS.DataElement;

  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} overflow-hidden h-full`}>
      {/* Header */}
      <div className="px-4 py-3 bg-white/60 border-b border-purple-100">
        <div className="flex items-center gap-2">
          <Database className={`w-5 h-5 ${colors.accent}`} />
          <h3 className={`font-semibold ${colors.accent}`}>Measure: Data Elements</h3>
        </div>
        <p className="text-xs text-neutral-500 mt-1">
          What to measure for progress
        </p>
      </div>

      {/* Content */}
      <div className="p-4 overflow-y-auto max-h-[400px]">
        {dataElements.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            {dataElements.map((item) => (
              <RelatedItemCard
                key={item.dataElement.element_id}
                title={item.dataElement.name}
                description={item.dataElement.description}
                type="DataElement"
                relationship={item.relationship}
                accentColor={colors.accent}
                borderColor={colors.border}
                bgColor="bg-white"
              >
                <div className="space-y-2">
                  {/* Data Type and Criticality */}
                  <div className="flex flex-wrap gap-2">
                    {item.dataElement.data_type && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          DATA_TYPE_STYLES[item.dataElement.data_type.toLowerCase()] ||
                          'bg-neutral-100 text-neutral-600'
                        }`}
                      >
                        {item.dataElement.data_type}
                      </span>
                    )}
                    {item.dataElement.criticality && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          CRITICALITY_STYLES[item.dataElement.criticality.toLowerCase()] ||
                          'bg-neutral-100 text-neutral-600'
                        }`}
                      >
                        {item.dataElement.criticality}
                      </span>
                    )}
                  </div>

                  {/* Source System */}
                  {item.dataElement.source_system && (
                    <div className="flex items-center gap-2">
                      <Server className="w-3 h-3 text-neutral-400" />
                      <span className="text-xs text-neutral-600">
                        Source: {item.dataElement.source_system}
                      </span>
                    </div>
                  )}

                  {/* Granularity */}
                  {item.dataElement.typical_granularity && (
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-3 h-3 text-purple-500" />
                      <span className="text-xs text-neutral-600">
                        Granularity: {item.dataElement.typical_granularity}
                      </span>
                    </div>
                  )}

                  {/* Required For */}
                  {item.dataElement.required_for && item.dataElement.required_for.length > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-3 h-3 text-orange-500 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-neutral-600 line-clamp-2">
                        Required for: {item.dataElement.required_for.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </RelatedItemCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="text-center py-6">
    <Database className="w-10 h-10 text-purple-200 mx-auto mb-2" />
    <p className="text-sm text-neutral-500">No data elements linked</p>
    <p className="text-xs text-neutral-400 mt-1">
      Data elements help track solution progress
    </p>
  </div>
);

export default DataElementsSection;
